import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Sparkles, Globe, LogOut, LayoutDashboard, Headphones, Mail } from "lucide-react";
import { LangMenu } from "./LangMenu";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import logo from "../assets/trimo-logo.png";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Header() {
  const { t, lang, toggleLang, L } = useLang();
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { key: "nav_sources", href: "#sources" },
    { key: "nav_how", href: "#how" },
    { key: "nav_features", href: "#features" },
    { key: "nav_pricing", href: "#pricing" },
  ];

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img src={logo} alt="Vitled" className="h-8 w-8 rounded-lg" />
          <span className="text-xl font-bold tracking-tight">
            Vit<span className="text-yellow-400">led</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <button
              key={item.key}
              onClick={() => go(item.href)}
              className="px-3.5 py-2 text-sm font-medium text-zinc-300 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
            >
              {t(item.key)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="support-button"
                className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-zinc-300 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
                aria-label="support"
              >
                <Headphones className="h-4 w-4" />
                <span>{L("Soporte", "Support")}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-950 border-white/10 text-white w-60">
              <div className="px-2 py-1.5">
                <p className="text-sm font-semibold">{L("Contactar", "Contact us")}</p>
                <p className="text-xs text-zinc-500">{L("Estamos aquí para ayudarte", "We're here to help")}</p>
              </div>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem asChild className="cursor-pointer focus:bg-white/5">
                <a href="mailto:vitledcom@gmail.com" data-testid="support-email" className="flex items-center">
                  <Mail className="h-4 w-4 mr-2 text-yellow-400" />
                  <span className="font-semibold">vitledcom@gmail.com</span>
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <LangMenu />

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-white/5 transition-colors">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full" />
                  ) : (
                    <span className="h-8 w-8 rounded-full bg-yellow-400/15 flex items-center justify-center text-yellow-400 font-bold">
                      {(user.name || "U")[0]}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-zinc-950 border-white/10 text-white">
                <div className="px-2 py-1.5 text-sm">
                  <p className="font-medium">{user.name}</p>
                  <p className="text-xs text-zinc-500 truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} className="cursor-pointer focus:bg-white/5">
                  <LayoutDashboard className="h-4 w-4 mr-2" /> {L("Mi panel", "Dashboard")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/studio")} className="cursor-pointer focus:bg-white/5">
                  <Sparkles className="h-4 w-4 mr-2" /> Studio
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={logout} className="cursor-pointer focus:bg-white/5 text-red-400">
                  <LogOut className="h-4 w-4 mr-2" /> {L("Cerrar sesión", "Log out")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              onClick={login}
              className="hidden sm:inline-flex px-3 py-2 text-sm font-semibold text-zinc-200 hover:text-white transition-colors"
            >
              {t("nav_signin")}
            </button>
          )}

          <Button
            onClick={() => navigate("/studio")}
            className="bg-yellow-400 hover:bg-yellow-300 text-zinc-950 font-semibold rounded-xl shadow-[0_0_24px_-6px_rgba(250,204,21,0.6)]"
          >
            <Sparkles className="h-4 w-4 mr-1.5" />
            {t("nav_studio")}
          </Button>
          <button
            className="md:hidden p-2 text-zinc-200"
            onClick={() => setOpen((v) => !v)}
            aria-label="menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-background/95 backdrop-blur-xl border-b border-white/10 px-5 py-4 flex flex-col gap-1">
          {navItems.map((item) => (
            <button
              key={item.key}
              onClick={() => go(item.href)}
              className="text-left px-3 py-2.5 text-sm font-medium text-zinc-300 hover:text-white rounded-lg hover:bg-white/5"
            >
              {t(item.key)}
            </button>
          ))}
          {!user && (
            <button
              onClick={login}
              className="text-left px-3 py-2.5 text-sm font-semibold text-yellow-400 rounded-lg hover:bg-white/5"
            >
              {t("nav_signin")}
            </button>
          )}
          <a
            href="mailto:vitledcom@gmail.com"
            data-testid="support-email-mobile"
            className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-zinc-300 rounded-lg hover:bg-white/5"
          >
            <Headphones className="h-4 w-4 text-yellow-400" />
            {L("Soporte", "Support")}: vitledcom@gmail.com
          </a>
        </div>
      )}
    </header>
  );
}
